import React, {
  createRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {ThesaurusAction} from 'types';
import type {ThesaurusFieldConfig} from 'sceneExtensions/extensions/thesaurusEdit/types';
import type {SetSourceValue} from 'sceneExtensions/types';
import type {ShowThesaurusCode} from 'schemaDefinition/types';
import type {FindAdditionalData} from 'services/thesaurus/functions/types';
import type {
  ThesaurusId,
  ThesaurusNode,
  ThesaurusNodeType,
  ThesaurusValue as ThesaurusValueType,
} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {HeaderHeight} from 'theme/settings';
import {useThesaurus, useTrimmedThesaurus} from 'services/thesaurus';
import {getThesaurusMaps} from 'services/thesaurus/functions/getThesaurusMaps';
import {SearchInput} from 'components/input';
import {FlexBox, Scrollable} from 'components/layout';
import {SceneTitle} from 'components/scene/SceneTitle';
import {Spacer} from 'components/spacer';
import {Text} from 'components/text';
import {CategoryContainer} from './components/CategoryContainer';
import {SearchingTopLevelsContainer} from './components/SearchingTopLevelsContainer';
import {ThesaurusToolbar} from './components/ThesaurusToolbar';
import {TopLevelsContainer} from './components/TopLevelsContainer';
import {
  ThesaurusEditStateContext,
  useThesaurusEditStateContextProviderValue,
} from './contexts';

type Props = {
  thesaurusId: ThesaurusId;
  showCode?: ShowThesaurusCode;
  includeDeactivatedValues: boolean;
  selectableNodeTypes?: string[];
  focusNodeCodeWithTimestamp?: string;
  fieldConfig?: ThesaurusFieldConfig;
  thesaurusValue?: ThesaurusValueType;
  onSetSourceValue: SetSourceValue<
    ThesaurusValueType,
    ThesaurusAction,
    ThesaurusNode
  >;
  /**
   * Custom function for resolving additional data for the added node,
   * matching the findNodeWithAncestors signature. Defaults to
   * findNodeWithAncestors when not provided.
   */
  findAdditionalData?: FindAdditionalData;
  onClose: () => void;
};

export const ThesaurusEdit: React.FC<Props> = ({
  thesaurusId,
  showCode = false,
  includeDeactivatedValues,
  selectableNodeTypes,
  focusNodeCodeWithTimestamp,
  fieldConfig,
  thesaurusValue,
  onSetSourceValue,
  findAdditionalData,
  onClose,
}) => {
  const {t, tLoose} = useLocalization();
  const fullThesaurus = useThesaurus(thesaurusId);
  // Initialize lazy maps for given thesaurus
  useRef(getThesaurusMaps(fullThesaurus));
  const trimmedThesaurus = useTrimmedThesaurus(
    fullThesaurus,
    thesaurusValue,
    includeDeactivatedValues,
  );

  const thesaurusEditState = useThesaurusEditStateContextProviderValue(
    trimmedThesaurus,
    showCode,
    includeDeactivatedValues,
    selectableNodeTypes as ThesaurusNodeType[] | undefined,
    thesaurusValue,
    focusNodeCodeWithTimestamp,
    onSetSourceValue,
    onClose,
    findAdditionalData,
  );
  const {placeholder} = useMemo(
    () => ({
      placeholder: fieldConfig?.showCode
        ? t('search.thesaurus.placeholder.withCode')
        : t('search.thesaurus.placeholder'),
    }),
    [fieldConfig?.showCode, t],
  );

  const {
    searchQuery,
    setSearchQuery,
    state: {search, navigation},
  } = thesaurusEditState;

  /**
   * LayoutHeaderHeight is used to inform the below Scroll-component of the offset.
   * We initially just guess 200, but useEffect will update this to the correct amount.
   */
  const [layoutHeaderHeight, setLayoutHeaderHeight] = useState(200);
  const layoutHeaderRef = createRef<HTMLDivElement>();
  useLayoutEffect(() => {
    if (layoutHeaderRef && layoutHeaderRef.current) {
      setLayoutHeaderHeight(
        layoutHeaderRef.current.clientHeight + HeaderHeight * 2,
      );
    }
  }, [layoutHeaderRef]);

  return (
    <ThesaurusEditStateContext.Provider value={thesaurusEditState}>
      {/* Header with search input */}
      <FlexBox ref={layoutHeaderRef}>
        <SceneTitle
          title={
            thesaurusId
              ? tLoose(`extension.thesaurusEdit.${thesaurusId}.title`)
              : ''
          }
          tooltipTitle={
            fieldConfig?.showCode ? (
              <FlexBox>
                <Text variant="body1">
                  {t('search.thesaurus.tooltip.header')}
                </Text>
                <FlexBox>
                  <Text variant="body2">
                    {t('search.thesaurus.tooltip.description')}
                  </Text>
                  <Spacer size={1} />
                  <Text variant="body2">
                    {t('search.thesaurus.tooltip.example')}
                  </Text>
                </FlexBox>
              </FlexBox>
            ) : undefined
          }>
          <ThesaurusToolbar onClose={onClose} />
        </SceneTitle>
        <Spacer size={1} />
        <SearchInput
          value={searchQuery ?? ''}
          placeholder={placeholder}
          triggerFocus={fieldConfig}
          onChange={setSearchQuery}
        />
        <Spacer size={3} />
      </FlexBox>

      {/* Content, scrollable */}
      <Scrollable offset={layoutHeaderHeight}>
        {navigation === 'TopLevels' ? (
          search === 'NotSearching' ? (
            <TopLevelsContainer thesaurusId={thesaurusId} />
          ) : (
            <SearchingTopLevelsContainer />
          )
        ) : (
          <CategoryContainer />
        )}
      </Scrollable>
    </ThesaurusEditStateContext.Provider>
  );
};
